/*
  Maison Vinteji — moteur de mouvement (v14)

  Modules, tous indépendants et tous coupés sous prefers-reduced-motion :
    1. parallax   — translate léger de l'image dans un cadre clippé
    2. reveal     — entrée décalée au scroll (casse les murs de texte)
    3. sheen      — reflet spéculaire du verre qui suit le curseur
    4. tilt       — inclinaison 3D douce des cartes au survol
    5. magnetic   — les boutons se rapprochent du curseur
    6. condense   — le header se réduit au seul wordmark au scroll

  Contraintes tenues :
    · on n'anime que transform / opacity (aucun reflow, 60 fps)
    · un seul listener scroll partagé, en requestAnimationFrame
    · aucune dépendance externe
*/
(function () {
  'use strict';

  var reduceQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  var reduce = reduceQuery.matches;
  var canHover = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------------------------------------------------------------
     Boucle de scroll partagée
     --------------------------------------------------------------- */
  var scrollTasks = [];
  var ticking = false;

  function runScrollTasks() {
    ticking = false;
    for (var i = 0; i < scrollTasks.length; i++) scrollTasks[i]();
  }

  function onScroll() {
    if (!ticking) { requestAnimationFrame(runScrollTasks); ticking = true; }
  }

  function addScrollTask(fn) {
    scrollTasks.push(fn);
    if (scrollTasks.length === 1) {
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
    }
  }

  /* ---------------------------------------------------------------
     1. PARALLAX
     Le cadre clippe, l'image est pré-agrandie : le translate ne
     révèle jamais de bord et ne creuse aucun trou dans le flux.
     --------------------------------------------------------------- */
  function initParallax() {
    var AMP = 18;    /* amplitude verticale en px, volontairement tenue */
    var SCALE = 1.1; /* pré-agrandissement */

    var selectors = [
      '.banner-media',
      '.deal_banner .banner-media',
      '.grid-banner .banner-media',
      '.slideshow_swiper .swiper-slide',
      '.image-banner .banner-media',
      '.collection-banner .banner-media',
      '.shop-the-look .banner-media',
      '.image-gallery__item',
      '.video-section .video-wrapper',
      '.collection_list_with_banner .banner-media'
    ].join(',');

    var pairs = [];
    Array.prototype.forEach.call(document.querySelectorAll(selectors), function (w) {
      var img = w.querySelector('img');
      if (!img) return;
      w.classList.add('vj-parallax');
      pairs.push({ w: w, img: img });
    });
    if (!pairs.length) return;

    addScrollTask(function () {
      var vh = window.innerHeight || document.documentElement.clientHeight;
      for (var i = 0; i < pairs.length; i++) {
        var r = pairs[i].w.getBoundingClientRect();
        if (r.bottom < -40 || r.top > vh + 40) continue;
        /* -1 quand l'élément est en bas de l'écran, +1 quand il est en haut */
        var p = (r.top + r.height / 2 - vh / 2) / (vh / 2 + r.height / 2);
        p = p > 1 ? 1 : (p < -1 ? -1 : p);
        pairs[i].img.style.transform =
          'scale(' + SCALE + ') translate3d(0,' + (p * AMP).toFixed(1) + 'px,0)';
      }
    });
    onScroll();
  }

  /* ---------------------------------------------------------------
     2. REVEAL
     Entrée décalée. On cible les blocs de contenu narratif : c'est
     ce qui empêche trois sections de texte de se lire comme un mur.
     --------------------------------------------------------------- */
  function initReveal() {
    if (!('IntersectionObserver' in window)) return;

    var groups = [
      /* en-têtes de section */
      '.vj-stats__head, .vj-grades__head, .vj-live__head, .vj-icones__head',
      /* corps de texte narratif */
      '.content-showcase .description, .content-showcase .long_text',
      '.vj-grades__desc, .vj-icones__intro',
      /* grilles : chaque enfant part avec son propre retard */
      '.vj-stats__item',
      '.vj-grade',
      '.vj-icones__card',
      '.text-with-icon .section-text-with-icon',
      '.testimonials .testimonial-wrapper',
      /* VJ-CORR-D229 — le récit « à propos ». LA CLASSE SE POSE ICI, JAMAIS
         DANS LE BALISAGE : la ligne `if (el.classList.contains('vj-reveal'))
         return;` ci-dessous s'en sert comme marque de « déjà traité ». Un
         élément qui arrive du gabarit avec la classe n'est jamais observé,
         n'entre pas dans `watched`, échappe donc AUSSI au filet de secours,
         et reste à opacity 0 pour toujours. Constaté le 8 septembre sur les
         cinq blocs de maison-recit, invisibles sur toute la page. */
      '.vj-recit__souffle',
      '.vj-recit__volet'
    ];

    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-in');
        obs.unobserve(entry.target);
      });
    /* VJ-CORR-D154 — 4 septembre 2026 — LE DÉCLENCHEMENT REMONTE AVANT
       L'ÉCRAN.
       `-8%` RÉTRÉCISSAIT la zone de déclenchement : il fallait qu'un bloc
       soit entré de 56 px DANS l'écran, puis qu'il occupe 12 % de sa propre
       hauteur dans cette zone réduite, pour commencer seulement à
       apparaître. Sur un doigt qui lance la page, on arrive donc SUR un
       bloc qui n'a pas encore commencé.
       `+12%` fait l'inverse : la zone déborde de 83 px sous l'écran, et le
       bloc a déjà commencé à se poser quand il devient visible. */
    }, { threshold: 0.12, rootMargin: '0px 0px 12% 0px' });

    var watched = [];

    groups.forEach(function (sel) {
      var els = document.querySelectorAll(sel);
      Array.prototype.forEach.call(els, function (el, i) {
        if (el.classList.contains('vj-reveal')) return;
        el.classList.add('vj-reveal');
        /* le retard se réinitialise par groupe pour éviter les cascades trop longues */
        /* VJ-CORR-D154 — le retard d'entrée tombe de 110 à 60 ms, et son
           plafond de 5 à 3 crans : la dernière tuile d'une grille attendait
           550 ms AVANT de commencer une transition de 850. Une seconde et
           demie pour qu'un texte finisse d'arriver, c'est ce que Rachid
           décrit par « ça ne s'affiche pas ». Le décalage reste — c'est lui
           qui casse le mur de texte — il ne fait plus attendre. */
        el.style.setProperty('--vj-delay', (Math.min(i, 3) * 60) + 'ms');
        io.observe(el);
        watched.push(el);
      });
    });

    /* Filet de sécurité.
       .vj-reveal démarre à opacity 0 : si l'observateur ne rend jamais la
       main (onglet ouvert en arrière-plan, navigateur intégré Instagram ou
       TikTok qui gèle les callbacks, économiseur de batterie), le contenu
       resterait invisible pour de bon. On repasse donc derrière : tout
       élément déjà dans l'écran, ou resté caché trop longtemps, est révélé
       sans attendre l'observateur. */
    function rescue() {
      for (var i = watched.length - 1; i >= 0; i--) {
        var el = watched[i];
        if (el.classList.contains('is-in')) { watched.splice(i, 1); continue; }
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          el.classList.add('is-in');
          io.unobserve(el);
          watched.splice(i, 1);
        }
      }
    }

    document.addEventListener('visibilitychange', function () {
      if (!document.hidden) rescue();
    });
    addScrollTask(rescue);
    setTimeout(rescue, 1200);
  }

  /* ---------------------------------------------------------------
     3. SHEEN — reflet spéculaire du verre
     On écrit deux variables CSS ; le dégradé radial est dans le CSS.
     --------------------------------------------------------------- */
  function initSheen() {
    /* VJ-CORR-D350 — 14 septembre 2026 — LE REFLET NE SUIT PLUS LA SOURIS.
       Rachid, Notre histoire : « lorsque je survole le mot de la fondatrice,
       il y a une lumière blanche qui apparaît derrière avec la souris, qui
       suit la souris. Je ne veux pas de ça, retire-moi cet effet, il sert à
       rien. » C'était cet écouteur : il écrivait --vj-mx / --vj-my sous le
       pointeur pour le halo blanc de 420 px de `.vj-glass::before`. Le halo
       est retiré dans le CSS (bloc D350) et l'écouteur n'est plus posé.
       Rollback : retirer le `return` ci-dessous et le bloc CSS D350. */
    return;
    /* eslint-disable no-unreachable */
    if (!canHover) return;

    var selector = [
      '.vj-glass',
      '.vj-glass--smoked',
      '.vj-stats__item',
      '.vj-grade',
      '.vj-icones__card',
      '.testimonials .testimonial-content',
      '.vj-live__calendar'
    ].join(',');

    document.addEventListener('pointermove', function (e) {
      var el = e.target.closest ? e.target.closest(selector) : null;
      if (!el) return;
      var r = el.getBoundingClientRect();
      el.style.setProperty('--vj-mx', (((e.clientX - r.left) / r.width) * 100).toFixed(1) + '%');
      el.style.setProperty('--vj-my', (((e.clientY - r.top) / r.height) * 100).toFixed(1) + '%');
    }, { passive: true });
    /* eslint-enable no-unreachable */
  }

  /* ---------------------------------------------------------------
     4. TILT — inclinaison 3D au survol
     Amplitude faible (4°) : au-delà, l'effet devient un gadget.
     --------------------------------------------------------------- */
  function initTilt() {
    if (!canHover) return;

    var MAX = 4; /* degrés */
    var selector = [
      '.vj-stats__item',
      '.vj-grade',
      '.vj-icones__card',
      '.text-with-icon .section-text-with-icon'
    ].join(',');

    var cards = document.querySelectorAll(selector);
    Array.prototype.forEach.call(cards, function (card) {
      if (card.dataset.vjTilt) return;
      card.dataset.vjTilt = '1';
      card.classList.add('vj-tilt');

      /* VJ-CORR-D33 — 17 aout 2026 — LA ROTATION 3D EST RETIREE.
         « dans le cadran GRADE A il y a sur le cote droit une hachure jaunee
         qui apparait quand on le survole, ca clignote. »
         Une carte en `rotate3d` sous `backdrop-filter` reechantillonne a
         chaque image ce qui passe derriere elle — ici la lueur doree du
         substrat de section — et l'arete de 1 px de `::before` se met a
         papilloter sur le bord qui se rapproche. Le defaut est structurel,
         pas colorimetrique : aucune retouche de teinte ne l'enleve.
         La rotation part donc. Ce que Rachid demande au survol, c'est
         « un peu d'ombre pour avoir un effet » : c'est D33 §6 qui le fait,
         en `translateY` + ombre, sans re-rendu du fond.
         Le reflet speculaire du verre, lui, RESTE : il suit toujours la
         main par --vj-lx / --vj-ly, sans aucune rotation.
         Rollback : restaurer les trois lignes --vj-rx / --vj-ry / --vj-ty. */
      /* VJ-CORR-D299 — 12 septembre 2026 — LE REFLET CESSE DE SUIVRE LA MAIN.
         Rachid : « sur desktop tu as certains boutons qui ont un effet inutile
         genre METALLIQUE lorsqu'on le survole avec la souris, COMME UN
         MEDAILLON AVEC UN REFLET. Retire-moi ca, j'aime pas du tout. »
         C'est cet ecouteur : il ecrivait --vj-lx / --vj-ly a chaque mouvement
         du pointeur, et poussait --vj-lueur de .45 a 1. Le halo blanc du
         `.vj-verre-liquide::after` glissait donc sous le curseur — c'est
         exactement le medaillon qu'il decrit.
         LE VERRE NE PERD PAS SON REFLET, IL PERD SON SUIVI : le CSS garde ses
         valeurs de repos (28 % / 8 %, opacite .45), c'est-a-dire une source de
         lumiere FIXE en haut a gauche. Une matiere, plus une animation.
         Rollback : restaurer cet ecouteur. */
    });
  }

  /* ---------------------------------------------------------------
     5. MAGNETIC — les boutons vont vers le curseur
     --------------------------------------------------------------- */
  function initMagnetic() {
    if (!canHover) return;
    /* VJ-CORR-D299 — LES BOUTONS NE VONT PLUS VERS LE CURSEUR.
       Meme demande, meme phrase. Mesure du diff de pixels au survol, a 1 440,
       sur les cinq pages : « Decouvrir la collection » rendait un ecart moyen
       de 84,8 avec un ECART-TYPE de 114,4 — un changement non uniforme, donc
       une forme qui bouge, pas un simple assombrissement. Meme signature sur
       « Filtrer » (115,1 / 132,8).
       CE QUI RESTE AU SURVOL : D33 §6, `translateY` + ombre. Un bouton doit
       garder un retour visuel — le site est a 100 en accessibilite sur les
       cinq pages, et un survol muet le ferait tomber.
       Rollback : retirer le `return` ci-dessous. */
    return;

    /* eslint-disable no-unreachable */
    var PULL = 6; /* px max */
    /* On exclut les déclencheurs et les items de menu déroulant : un
       bouton qui se déplace sous le curseur rend un menu instable. */
    var btns = document.querySelectorAll(
      '.btn:not(.btn-link):not(.dropdown-toggle):not(.dropdown-item):not(.custom-select-buttons)'
    );

    Array.prototype.forEach.call(btns, function (btn) {
      if (btn.dataset.vjMag) return;
      btn.dataset.vjMag = '1';

      btn.addEventListener('pointermove', function (e) {
        var r = btn.getBoundingClientRect();
        var cx = (e.clientX - r.left) / r.width - 0.5;
        var cy = (e.clientY - r.top) / r.height - 0.5;
        btn.style.setProperty('--vj-magx', (cx * PULL * 2).toFixed(1) + 'px');
        btn.style.setProperty('--vj-magy', (cy * PULL * 2 - 2).toFixed(1) + 'px');
      }, { passive: true });

      btn.addEventListener('pointerleave', function () {
        btn.style.setProperty('--vj-magx', '0px');
        btn.style.setProperty('--vj-magy', '0px');
      });
    });
    /* eslint-enable no-unreachable */
  }

  /* ---------------------------------------------------------------
     6. CONDENSE — header réduit au seul wordmark
     Le wordmark glisse jusqu'au centre exact du volet ; la nav et
     les sélecteurs s'effacent. Le calcul de la course se fait sans
     transform appliquée, pour mesurer la position au repos.
     --------------------------------------------------------------- */
  function initCondense() {
    /* Le repere peut etre le mot « MA BOUTIQUE » OU un logo image : des que
       la maison depose son logo dans l'admin, le theme cesse d'ecrire le
       wordmark. Les deux doivent glisser de la meme facon. */
    var mark = document.querySelector('header .wdt-logo-container .wdt-logo-title')
           || document.querySelector('header .header-title .wdt-logo-title')
           || document.querySelector('header .wdt-logo-container .header-logo img')
           || document.querySelector('header .wdt-logo-container .header-logo svg');
    if (!mark) return;
    mark.classList.add('vj-wordmark');

    var deskQuery = window.matchMedia('(min-width: 992px)');
    var row = mark.closest('.wdt-header-content') || mark.closest('header');
    if (!row) return;

    var RUN = 320;          /* la course se termine apres 320 px de scroll */
    var CONDENSE_AT = 0.55; /* seuil d'effacement de la nav */
    var delta = null;
    var lastCondensed = null;

    /* Le defilement ne se lit pas au meme endroit selon le navigateur et
       selon l'element qui porte reellement la barre (documentElement sur
       Safari iOS, body sur certains rendus embarques). On prend la plus
       grande des trois valeurs : une seule d'entre elles est non nulle. */
    function scrollPos() {
      return Math.max(
        window.scrollY || window.pageYOffset || 0,
        (document.documentElement && document.documentElement.scrollTop) || 0,
        (document.body && document.body.scrollTop) || 0
      );
    }

    /* Position au repos SANS toucher au transform.
       Piege corrige : `.wdt-logo-title` porte une transition sur transform.
       Mettre transform='none' puis lire getBoundingClientRect renvoyait la
       position ANIMEE en cours, pas la position au repos, ce qui faisait
       osciller le centrage. offsetLeft, lui, est une valeur de mise en page :
       les transforms ne l'affectent pas. */
    function restingCenterX(el) {
      var x = 0, node = el;
      while (node) { x += node.offsetLeft || 0; node = node.offsetParent; }
      return x + el.offsetWidth / 2;
    }

    function measure() {
      /* Mesurer avant que la police soit substituee donnait une largeur
         fausse : le mot atterrissait a cote du centre. Tant que l'element
         n'a pas de largeur, on ne fige rien. */
      if (!mark.offsetWidth) { delta = null; return; }
      var viewportCenter = (window.innerWidth || document.documentElement.clientWidth) / 2;
      delta = viewportCenter - restingCenterX(mark);
    }

    function update() {
      if (reduce) {
        mark.style.transform = 'none';
        if (lastCondensed) {
          document.body.classList.remove('vj-header-condensed');
          lastCondensed = false;
        }
        return;
      }

      if (delta === null) measure();
      if (delta === null) return;   /* pas encore mesurable : on repassera */

      var p = Math.min(scrollPos() / RUN, 1);
      mark.style.transform = 'translateX(' + (delta * p).toFixed(2) + 'px)';

      /* VJ-CORR-A2-JS-DEBUT — 16 aout 2026 — l'etat condense s'applique aussi
         sous 992 px.
         La garde d'origine renvoyait ici en mobile, ce qui empechait l'etat
         condense d'exister. Decision du proprietaire du 16 aout : le mobile
         doit reproduire le comportement desktop, logo seul au centre et les
         quatre pictogrammes effaces pendant le scroll. Le retour se fait
         naturellement en remontant, l'etat condense se retirant sous le seuil.
         `measure()` est inchangee : elle vise deja le milieu du viewport, ce
         qui est correct puisque plus rien n'occupe le centre.
         ROLLBACK : supprimer ce bloc et retablir les lignes d'origine,
         conservees mot pour mot ci-dessous.

         ---- code d'origine ----
         if (!deskQuery.matches) {
           if (lastCondensed) {
             document.body.classList.remove('vj-header-condensed');
             lastCondensed = false;
           }
           return;
         }
         ------------------------ */
      /* VJ-CORR-A2-JS-FIN */

      var condensed = p >= CONDENSE_AT;
      if (condensed !== lastCondensed) {
        document.body.classList.toggle('vj-header-condensed', condensed);
        lastCondensed = condensed;
      }
    }

    function remesurer() { delta = null; update(); }

    addScrollTask(update);

    /* Toutes les occasions ou la mesure devient fausse. La police substituee
       est la plus sournoise : elle change la largeur du mot APRES le premier
       calcul, sans declencher ni scroll ni resize. */
    window.addEventListener('resize', remesurer);
    window.addEventListener('orientationchange', remesurer);
    window.addEventListener('load', remesurer);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(remesurer);
    if (deskQuery.addEventListener) deskQuery.addEventListener('change', remesurer);
    if (reduceQuery.addEventListener) {
      reduceQuery.addEventListener('change', function (e) { reduce = e.matches; remesurer(); });
    }
    /* Filet : si la mise en page arrive tard (images du header, menu monte
       par theme.js), on repasse deux fois sans rien attendre de personne. */
    setTimeout(remesurer, 400);
    setTimeout(remesurer, 1500);

    update();
  }

  /* ---------------------------------------------------------------
     Indice de balayage
     ---------------------------------------------------------------
     Un carrousel tactile ne dit pas qu'il est un carrousel : sur
     mobile, rien ne signale qu'un contenu se fait glisser. On pose
     donc sous chaque carrousel a plusieurs vues un marqueur discret
     (pictogramme de balayage + libelle), qui disparait des la
     premiere interaction — il a rempli son role a ce moment-la.
     --------------------------------------------------------------- */
  /* VJ-CORR-D211 — 7 septembre 2026 — OÙ SE POSE L'INDICE « FAITES GLISSER »
     Rend le nœud devant lequel insérer l'indice pour qu'il arrive APRÈS tous
     les repères déjà posés sous un rail : les puces de la maison (D202) comme
     la pagination propre à Swiper. Sans lui, l'indice se range à la place que
     le hasard du DOM lui laisse — et l'audit du 7 septembre a mesuré le
     diaporama de l'accueil avec l'indice AU-DESSUS de ses pastilles, à contre-
     sens des six autres rangées. */
  function apresLesReperes(rail, puces) {
    var dernier = puces || rail;
    var n = dernier.nextElementSibling;
    while (n && n.classList &&
           (n.classList.contains('vj-puces') || n.classList.contains('swiper-pagination'))) {
      dernier = n;
      n = n.nextElementSibling;
    }
    return dernier.nextSibling;
  }

  var SWIPE_ICON =
    '<svg viewBox="0 0 256 256" aria-hidden="true" focusable="false">' +
    '<path d="M112 128V48a16 16 0 0 1 32 0v80" />' +
    '<path d="M144 112a16 16 0 0 1 32 0v16" />' +
    '<path d="M176 128a16 16 0 0 1 32 0v32a72 72 0 0 1-72 72h-16a72 72 0 0 1-62-35l-22-38a16 16 0 0 1 28-16l14 24" />' +
    '<path d="M112 128V96" />' +
    '<path d="M52 44 36 60l16 16" /><path d="M204 44l16 16-16 16" />' +
    '<path d="M36 60h184" />' +
    '</svg>';

  /* VJ-CORR-D159 — LA SECTION QUI PORTE UN INDICE SE DECLARE.
     Rachid : « l'espace que tu laisses a chaque fois qu'il y a des swipe
     icon et quand il y en a pas, c'est pas le meme ; important d'avoir le
     meme espace lorsqu'on passe d'une section a l'autre. »

     Mesure a 402 px : deux sections voisines se joignent par 26 px de bas
     + 26 px de haut, soit 52 px. Une section terminee par un indice y
     ajoutait la boite de l'indice — 17,6 + 26 + 5,6 = 49,2 px — et le
     joint passait a 101 px : deux fois plus large, une section sur deux.

     On ne peut pas le savoir en CSS seul : l'indice est cree par ce
     script, apres coup. La classe posee ici donne au CSS la prise qui lui
     manque, et le bloc D159 de vj-premium.css fait rendre a l'indice
     exactement les 26 px de rembourrage bas qu'il remplace. */
  function marquerSection(el) {
    var sec = el.closest && el.closest('.shopify-section');
    if (sec) sec.classList.add('vj-a-indice');
  }


  /* ---------------------------------------------------------------
     VJ-CORR-D202 — 6 septembre 2026 — DES PUCES PARTOUT OÙ L'ON GLISSE
     ---------------------------------------------------------------
     Rachid : « sur le luxe qui se transmet, le fait de glisser, là il y a
     des bulles qui indiquent qu'on a slidé, donc c'est très bien ça, je
     pense qu'on devrait appliquer partout dès lors qu'il y a le fait de
     glisser. Pas de chiffres, mais comme t'as fait ; c'est juste que là
     il est pas visible donc on voit pas. »  Et, séparément : « peut-être
     aussi sur les photos qu'on va slider — non, ça laisse tomber. »

     INVENTAIRE FAIT AVANT D'ÉCRIRE UNE LIGNE, sur les cinq pages, à
     402 px. Sept rangées se font glisser sur ce site ; UNE SEULE portait
     des puces — le diaporama d'accueil, celui qu'il cite en exemple :

         accueil    diaporama                 3 puces
         accueil    la sélection du moment    aucune
         accueil    comprendre nos grades     aucune
         catalogue  explorer nos collections  aucune
         fiche      dans le même esprit       aucune
         histoire   la frise                  aucune
         histoire   icônes racontées          aucune

     Il ne demandait donc pas une retouche, il demandait six rangées.

     UNE SEULE MÉCANIQUE POUR DEUX FAMILLES DE RANGÉES. Le site en a deux,
     qui ne se déplacent pas de la même façon : les carrousels Swiper
     bougent par `transform`, les rails maison par `scrollLeft`. Une puce
     qui écouterait `scroll` resterait donc morte sur les premiers. Le
     dénominateur commun est l'AVANCEMENT, un nombre de 0 à 1 : Swiper le
     publie sous `progress`, un rail se le calcule. Le reste du code ne
     sait plus de quelle famille il s'occupe.

     COMBIEN DE PUCES. Une par vue, pas une par élément : « la sélection
     du moment » a douze pièces mais les montre par deux, douze puces ne
     diraient rien à personne. Le compte est donc `éléments ÷ par vue`,
     plafonné à huit — au-delà, une rangée de points cesse d'être un
     repère et devient un motif.

     CE QUI N'EN REÇOIT PAS, ET C'EST VOULU :
       · les photos de fiche produit — il l'a explicitement retiré ;
       · les cartes produit, qui ont déjà leurs propres points ;
       · le diaporama, qui a déjà ceux de Swiper — on ne lui en ajoute
         pas une seconde rangée, on rend les siens visibles en CSS (bloc
         D202 de vj-premium.css).

     Les puces sont des TÉMOINS, pas des commandes : `aria-hidden`, aucun
     élément focalisable. La rangée reste parcourable au clavier par le
     défilement natif, et l'indice « faites glisser » dit déjà le geste.
     C'est la leçon de D186 — un `role` posé sur un conteneur pour faire
     joli avait orphelin ses enfants et coûté cinq points d'accessibilité.
     --------------------------------------------------------------- */
  function poserPuces(rail) {
    if (rail.dataset.vjPuces) return null;

    /* Ce qui est exclu, et pourquoi — voir l'en-tête du bloc. */
    if (rail.closest('.card-wrapper, product-card, .vj-carte-carrousel, [class*="product-media"]')) return null;
    var sec = rail.closest('.shopify-section');
    if (sec && sec.querySelector('.swiper-pagination .swiper-pagination-bullet')) return null;

    var swiper = rail.swiper || null;
    var slides = rail.querySelectorAll(':scope > .swiper-wrapper > .swiper-slide');
    var total = slides.length ? slides.length : rail.children.length;
    if (total < 2) return null;

    var parVue = 1;
    if (swiper && swiper.params && swiper.params.slidesPerView) {
      var v = swiper.params.slidesPerView;
      parVue = (v === 'auto') ? 1 : Math.max(1, Math.round(v));
    } else if (rail.children[0]) {
      var large = rail.children[0].getBoundingClientRect().width;
      if (large > 0) parVue = Math.max(1, Math.round(rail.clientWidth / large));
    }

    var pages = Math.max(2, Math.ceil(total / parVue));
    if (pages > 8) pages = 8;

    rail.dataset.vjPuces = '1';
    var boite = document.createElement('div');
    boite.className = 'vj-puces';
    boite.setAttribute('aria-hidden', 'true');
    for (var i = 0; i < pages; i++) {
      var pt = document.createElement('span');
      pt.className = 'vj-puces__pt' + (i === 0 ? ' est-active' : '');
      boite.appendChild(pt);
    }
    (rail.parentElement || rail).insertBefore(boite, rail.nextSibling);

    var pts = boite.children;
    var dernier = 0;
    var attente = 0;

    function avancement() {
      if (swiper && typeof swiper.progress === 'number') {
        return Math.min(1, Math.max(0, swiper.progress));
      }
      var course = rail.scrollWidth - rail.clientWidth;
      return course > 1 ? rail.scrollLeft / course : 0;
    }

    function peindre() {
      attente = 0;
      var i = Math.round(avancement() * (pages - 1));
      if (i === dernier || !pts[i]) return;
      pts[dernier].classList.remove('est-active');
      pts[i].classList.add('est-active');
      dernier = i;
    }

    /* Une image d'animation suffit : le doigt produit des dizaines
       d'evenements par seconde, la puce n'a besoin que d'un repaint. */
    function demander() {
      if (!attente) attente = window.requestAnimationFrame(peindre);
    }

    if (swiper && typeof swiper.on === 'function') {
      swiper.on('progress', demander);
      swiper.on('slideChange', demander);
    }
    rail.addEventListener('scroll', demander, { passive: true });
    return boite;
  }

  function initSwipeHints() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    document.querySelectorAll('.swiper').forEach(function (el) {
      if (el.dataset.vjSwipeHint) return;

      var slides = el.querySelectorAll('.swiper-slide');
      if (slides.length < 2) return;

      /* Un carrousel qui montre deja tout n'a rien a faire glisser. */
      var instance = el.swiper;
      if (instance && instance.params && instance.params.slidesPerView >= slides.length) return;

      el.dataset.vjSwipeHint = '1';

      var hint = document.createElement('div');
      hint.className = 'vj-swipe-hint';
      hint.setAttribute('aria-hidden', 'true');
      hint.innerHTML = SWIPE_ICON + '<span>Faites glisser</span>';
      var pucesSwiper = poserPuces(el);
      /* VJ-CORR-D211 — 7 septembre 2026 — L'INDICE SE POSE APRÈS TOUS LES REPÈRES.
         `poserPuces` rend null quand le rail porte déjà sa propre pagination —
         c'est le cas du diaporama de l'accueil, qui garde les pastilles de
         Swiper. On retombait alors sur `el.nextSibling`, et depuis que D207 a
         sorti cette pagination du rail pour qu'elle cesse d'être sciée, ce
         frère-là EST la pagination : l'indice se glissait AU-DESSUS d'elle,
         seul endroit du site où l'ordre s'inversait. `apresLesReperes` cherche
         le dernier repère déjà posé, quel que soit son nom. */
      (el.parentElement || el).insertBefore(hint, apresLesReperes(el, pucesSwiper));
      marquerSection(el);

      /* VJ-CORR-D159-DEBUT — 5 septembre 2026 — L'INDICE NE S'EFFACE PLUS.
         Rachid : « chaque fois que je swipe quelque chose ils disparaissent,
         et donc on ne sait pas qu'on peut swiper ; il faut laisser le swipe
         icon quoi qu'il arrive, meme quand on fait glisser vers la droite
         vers la gauche, il faut qu'il reste. »

         L'ancienne version posait `is-gone` a la premiere interaction
         humaine (touchstart / mousedown / sliderMove). Le raisonnement
         d'alors — « il a rempli son role » — est faux sur un carrousel
         qu'on reparcourt : une fois efface, plus rien ne dit qu'il reste
         des vues a droite. Et comme `.is-gone` ne fait qu'eteindre
         l'opacite, sa boite restait dans le flux : le geste ne supprimait
         pas l'indice, il le remplacait par une bande vide de 49 px. C'est
         exactement l'« espace qui sert a rien » releve sous « Icones
         racontees ».

         Aucun ecouteur n'est donc pose ici. `instance` reste lue plus haut
         pour le test « le carrousel montre-t-il deja tout ». */
    });
  }
  /* VJ-CORR-D159-FIN */

  /* ---------------------------------------------------------------
     Rails natifs a defilement automatique
     ---------------------------------------------------------------
     Certains blocs ne sont pas des carrousels Swiper mais de simples
     conteneurs a `overflow-x` avec aimantation (`[data-vj-scroller]`).
     Ils recoivent le meme traitement : marqueur de balayage, et une
     avance automatique qui s'arrete des que la personne prend la main.
     --------------------------------------------------------------- */
  /* ---------------------------------------------------------------
     Bandeaux defilants (marques)
     ---------------------------------------------------------------
     Un carrousel a paliers ne se lit pas comme « ca defile » : il
     reste immobile la plupart du temps, et le navigateur suspend son
     minuteur des que l'onglet passe en arriere-plan. On enveloppe donc
     les elements dans une piste, on les duplique une fois, et on anime
     la piste en CSS : le mouvement est continu et ne depend d'aucun
     minuteur. La copie est masquee aux lecteurs d'ecran.
     --------------------------------------------------------------- */
  function initMarquees() {
    document.querySelectorAll('[data-vj-marquee]').forEach(function (el) {
      if (el.dataset.vjMarqueeInit) return;
      var items = Array.prototype.slice.call(el.children);
      if (items.length < 2) return;
      el.dataset.vjMarqueeInit = '1';

      var track = document.createElement('div');
      track.className = 'vj-marquee__track';
      items.forEach(function (it) { track.appendChild(it); });

      var copy = track.cloneNode(true);
      copy.setAttribute('aria-hidden', 'true');
      copy.querySelectorAll('a, button').forEach(function (a) { a.setAttribute('tabindex', '-1'); });

      el.appendChild(track);
      el.appendChild(copy);

      /* La duree suit le nombre d'elements : la vitesse percue reste
         constante quel que soit le nombre de maisons affichees. */
      el.style.setProperty('--vj-marquee-duration', (items.length * 4.5) + 's');
    });
  }

  function initScrollers() {
    if (!window.matchMedia('(max-width: 749px)').matches) return;

    document.querySelectorAll('[data-vj-scroller]').forEach(function (rail) {
      if (rail.dataset.vjScrollerInit) return;
      var cartes = rail.children;
      if (cartes.length < 2) return;
      rail.dataset.vjScrollerInit = '1';

      var hint = document.createElement('div');
      hint.className = 'vj-swipe-hint';
      hint.setAttribute('aria-hidden', 'true');
      hint.innerHTML = SWIPE_ICON + '<span>Faites glisser</span>';
      var pucesRail = poserPuces(rail);
      rail.parentElement.insertBefore(hint, apresLesReperes(rail, pucesRail));   /* VJ-CORR-D211 */
      marquerSection(rail);

      var timer = null;
      var index = 0;

      /* VJ-CORR-D159 — `stop()` faisait DEUX choses : arreter l'avance
         automatique (ce qu'on garde, prendre la main doit rendre la main)
         et effacer l'indice (ce qu'on retire, voir le bloc D159 ci-dessus). */
      function stop() {
        if (timer) { window.clearInterval(timer); timer = null; }
      }

      function avancer() {
        /* Hors ecran, on ne fait rien : inutile d'animer un bloc que
           personne ne regarde. */
        var boite = rail.getBoundingClientRect();
        if (boite.bottom < 0 || boite.top > window.innerHeight) return;

        index = (index + 1) % cartes.length;
        var cible = cartes[index];
        rail.scrollTo({
          left: cible.offsetLeft - (rail.clientWidth - cible.clientWidth) / 2,
          behavior: reduce ? 'auto' : 'smooth'
        });
      }

      /* VJ-CORR-D34 : 4500 ms donnait une cadence de defilante publicitaire.
         7500 ms laisse le temps de lire une carte avant la suivante. */
      /* VJ-CORR-D177 — 6 septembre 2026 — un rail peut demander l'indice de
       balayage SANS l'avance automatique. Sur une frise de RÉCIT, un chapitre
       qui s'en va tout seul au milieu d'une phrase est une faute : c'est la
       même que celle qui faisait tourner les photos d'une fiche produit
       (D175). L'indice, lui, reste — c'est lui qui dit qu'il y a une suite. */
    if (rail.hasAttribute('data-vj-sans-avance')) return;

    timer = window.setInterval(avancer, 7500);
      ['touchstart', 'mousedown', 'wheel'].forEach(function (evt) {
        rail.addEventListener(evt, stop, { passive: true, once: true });
      });
    });
  }

  /* ---------------------------------------------------------------
     Categories : aucune tuile n'est elue d'office
     ---------------------------------------------------------------
     `gallery-component` (theme.js) appelle setActiveBlock(0) au montage :
     la premiere tuile — « Sacs iconiques » — arrivait donc deja coloree,
     comme si l'on avait choisi a la place du visiteur. Or la meme classe
     `highlight` sert a DEUX choses : reveler l'image de fond (opacity 0
     sinon) et colorer la tuile. On garde donc l'image, on retire la
     couleur : la tuile ne se signale qu'au survol ou au toucher.
     --------------------------------------------------------------- */
  /* VJ-CORR-D407 — 19 septembre 2026 — LES PHOTOS D'UNE TUILE DE CATEGORIE
     TOURNENT, ET LE PREMIER VISAGE N'EST PAS TOUJOURS LE MEME.
     ---------------------------------------------------------------
     Rachid : « quand la personne est sur les sacs, qu'il y ait plusieurs
     images qui tournent automatiquement, que ce ne soit pas tout le temps
     le meme qui soit affiche. Pareil pour la petite maroquinerie et pour
     les accessoires — qu'il y ait un petit effet. »

     Le gabarit empile jusqu'a cinq photos de pieces DISPONIBLES ; ici on
     les fait se fondre. Trois precautions, chacune payee ailleurs :

     * LE TIRAGE SE FAIT CHEZ LE VISITEUR. Un tirage au serveur est mis en
       cache au bord du reseau et redonne « tout le temps la meme » —
       c'est §5.2 centquattuordecies, paye sur la selection du moment
       (D398). Ici, `Math.random()` dans le navigateur.
     * LES TROIS TUILES NE CHANGENT PAS EN MEME TEMPS. Depart decale de
       1,2 s par rang : trois photos qui basculent d'un bloc font un
       clignotement, pas un effet.
     * RIEN NE TOURNE HORS DE L'ECRAN ni onglet cache : une vitrine qu'on
       ne regarde pas n'a pas a telecharger ni a peindre.

     Une vitrine peut defiler (§5.2 tervicies) : une tuile de categorie
     MONTRE un rayon, elle ne se lit pas et elle ne s'examine pas.

     Rollback : retirer cette fonction et son appel dans init(). */
  function initPhotosCategories() {
    if (document.documentElement.classList.contains('vj-leger')) return;   /* D465 */
    var piles = document.querySelectorAll('[data-vj-cat-photos]');
    if (!piles.length) return;

    piles.forEach(function (pile, rang) {
      /* VJ-CORR-D417 — la fonction est rappelee a chaque `shopify:section:load`
         (editeur de theme). Sans garde, une pile recevait un SECOND minuteur a
         chaque rendu : les photos doublaient de vitesse a chaque retouche, et
         rien ne le disait. Le drapeau sert deja de marqueur pour le CSS ; il
         sert aussi de garde. */
      if (pile.dataset.vjCatPrete === '1') return;
      var photos = pile.querySelectorAll('.vj-cat-photo');
      pile.dataset.vjCatPrete = '1';
      if (!photos.length) return;

      /* Le tirage : une tete au hasard, et on part de la. */
      var vue = Math.floor(Math.random() * photos.length);
      for (var i = 0; i < photos.length; i++) photos[i].classList.toggle('est-vue', i === vue);
      if (photos.length < 2 || reduce) return;

      var aLEcran = true;
      if (window.IntersectionObserver) {
        aLEcran = false;
        new IntersectionObserver(function (e) {
          e.forEach(function (x) { aLEcran = x.isIntersecting; });
        }, { threshold: 0.25 }).observe(pile);
      }

      var tourner = function () {
        if (!aLEcran || document.hidden) return;
        photos[vue].classList.remove('est-vue');
        vue = (vue + 1) % photos.length;
        photos[vue].classList.add('est-vue');
      };

      /* VJ-CORR-D448 — 21 septembre 2026 — ON RALENTIT, ET LA VALEUR N'EST
         PAS INVENTEE : C'EST CELLE DE LA MAISON.
         Rachid : « pour tout ce que tu as mis comme effet dans Explorer par
         categorie, tu fais defiler les images un peu trop vite. Ralentis, il
         faut qu'on ait le temps de voir. »
         D407 avait pose 4 200 ms. Or la maison a deja tranche cette question,
         et deux fois : D34 ecrit noir sur blanc « 4 500 ms donnait une cadence
         de defilante publicitaire ; 7 500 ms laisse le temps de lire une carte
         avant la suivante », et `VJ_POSE` vaut 7 500 pour les carrousels.
         **Les tuiles tournaient donc PLUS VITE que la cadence que le dossier
         avait deja jugee trop rapide.** On s'aligne sur 7 500 (§5.2
         centoctovicies : la valeur se RELEVE sur le reste du site, elle ne
         s'invente pas). Pose reelle : 7 500 - 1 100 de fondu = 6 400 ms.
         La premiere bascule passe de 2 600 a `VJ_ATTENTE` (4 000 ms) : « rien
         ne bouge pendant les N premieres ms ». Le decalage de 1 200 ms par
         rang reste — D407 : trois photos qui basculent d'un bloc font un
         clignotement, pas un effet. */
      window.setTimeout(function () {
        tourner();
        window.setInterval(tourner, 7500);
      }, 4000 + rang * 1200);
    });
  }

  function initCategories() {
    document.querySelectorAll('.collection-list-with-banner').forEach(function (sec) {
      var tuiles = sec.querySelectorAll('.gallery-content .content-item');
      if (!tuiles.length) return;

      /* ---------------------------------------------------------------
         VJ-CORR-D400 — 19 septembre 2026 — AU TELEPHONE, LE MARRON SUIT
         LE DOIGT AU LIEU D'ATTENDRE QU'ON APPUIE
         ---------------------------------------------------------------
         Rachid, 19 septembre : « sur la version mobile, quand on passe
         d'une categorie a une autre, le truc marron n'apparait que si
         j'appuie sur la categorie. Or moi, des que je veux slider, je
         veux que ca devienne marron : d'abord le marron est fixe sur les
         sacs, puis des que je slide a droite c'est petite maroquinerie
         qui prend le marron, et pareil pour accessoires. »

         MESURE AVANT (tmp-r22-categories.js, 402 px) : au repos, les
         trois tuiles rendent un fond transparent et une encre #2D201A ;
         au premier survol, la tuile prend #473931, l'encre passe creme
         et le bouton « Decouvrir » apparait. Le marron existait donc
         bien — il n'etait juste accroche a RIEN sur un ecran tactile,
         ou le survol n'existe pas et ou le seul appui possible ouvre la
         collection. La rangee glisse pourtant : 868 px de contenu pour
         402 de fenetre.

         CE QUI CHANGE, ET SEULEMENT LA. La regle « aucune tuile n'est
         elue d'office » ci-dessous reste vraie POUR L'ORDINATEUR, ou la
         colonne ne glisse pas et ou le survol dit qui l'on vise. Au
         telephone, la tuile au centre du rail est celle qu'on regarde :
         c'est elle qui porte le marron, des le premier coup d'oeil et
         sans qu'on ait rien a faire.

         La mesure est faite dans un `requestAnimationFrame` : un
         ecouteur de defilement qui lit des rectangles a chaque pixel
         fait chauffer le telephone pour rien (meme raison que le
         guetteur unique de vj-carte-carrousel.js).
         Rollback : retirer ce bloc — `degager()` reprend seul la main. */
      var rail = sec.querySelector('.gallery-content .content-section');
      if (rail && rail.scrollWidth > rail.clientWidth + 8) {
        var enAttente = false;
        var dernier = -1;

        var suivre = function () {
          enAttente = false;
          var boite = rail.getBoundingClientRect();
          var centre = boite.left + boite.width / 2;
          var gagnante = 0;
          var ecart = Infinity;
          for (var i = 0; i < tuiles.length; i++) {
            var b = tuiles[i].getBoundingClientRect();
            var d = Math.abs(b.left + b.width / 2 - centre);
            if (d < ecart) { ecart = d; gagnante = i; }
          }
          /* On repose les classes a chaque passage, meme si la gagnante n'a
             pas change : `gallery-component` (theme.js) les retire toutes des
             qu'un survol passe ailleurs, et une garde sur « c'est deja la
             bonne » laisserait alors la rangee sans aucun marron. */
          dernier = gagnante;
          for (var k = 0; k < tuiles.length; k++) {
            tuiles[k].classList.toggle('highlight', k === gagnante);
          }
        };
        var reveiller = function () {
          if (enAttente) return;
          enAttente = true;
          requestAnimationFrame(suivre);
        };

        rail.addEventListener('scroll', reveiller, { passive: true });
        window.addEventListener('resize', reveiller);
        suivre();
        /* Les polices et les photos changent les largeurs apres coup :
           on repasse, comme le fait vj-cesures.js pour ses traits. */
        setTimeout(suivre, 400);
        setTimeout(suivre, 1400);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(suivre);
        return;
      }

      function degager() {
        if (sec.dataset.vjTouche) return;      /* le visiteur a choisi : on se retire */
        tuiles.forEach(function (t) { t.classList.remove('highlight'); });
      }

      /* Des la premiere intention reelle, on rend la main a theme.js. */
      ['pointerdown', 'pointerenter', 'touchstart', 'focusin'].forEach(function (evt) {
        sec.addEventListener(evt, function () { sec.dataset.vjTouche = '1'; }, { passive: true, once: true });
      });

      degager();
      /* theme.js monte son composant apres nous selon l'ordre de chargement :
         on repasse, sans jamais contrarier une vraie interaction. */
      requestAnimationFrame(degager);
      setTimeout(degager, 300);
      setTimeout(degager, 1200);
    });
  }

  /* ---------------------------------------------------------------
     VJ-CORR-D143 — `initLivre()` A ETE SUPPRIMEE
     ---------------------------------------------------------------
     Elle branchait un balayage sur `<vinteji-book>` en CLIQUANT sur le
     bouton « Page suivante ». Cette section a son PROPRE gestionnaire
     de geste qui faisait deja `go(index + 1)` : un doigt valait donc
     deux avances, et `connectedCallback` se rejouant a chaque
     deplacement dans le DOM, les ecouteurs s'empilaient. D'ou le saut
     1 -> 3 -> 6 que Rachid decrivait.

     Le livre est remplace par « le fil » (accordeon, un seul etat,
     aucun geste) : il n'y a plus rien a brancher ici.
     Rollback : `git show <commit avant D143>:assets/vj-motion.js`.
     --------------------------------------------------------------- */

  /* ---------------------------------------------------------------
     Chat en direct et icone du compte
     ---------------------------------------------------------------
     Deux petites choses qui ne meritent pas de module a elles seules.
     · Le widget Shopify Inbox arrive apres nous, dans son propre
       cadre : on l'attend et on signale sa presence au CSS, qui
       decale alors le bouton « retour en haut ».
     · L'icone du compte mene hors du domaine de la boutique (service
       d'authentification Shopify). Ce trajet est long et le theme n'y
       peut rien ; il peut en revanche accuser reception du clic.
     --------------------------------------------------------------- */
  function initChatEtCompte() {
    /* 1. Presence du chat. On observe au lieu de sonder en boucle. */
    function verifierChat() {
      var trouve = document.querySelector(
        '#shopify-chat, [id^="shopify-chat"], .shopify-chat-container, iframe[title*="chat" i], iframe[name*="chat" i]'
      );
      if (trouve) {
        document.body.classList.add('vj-chat-present');
        return true;
      }
      return false;
    }
    if (!verifierChat() && 'MutationObserver' in window) {
      var mo = new MutationObserver(function () {
        if (verifierChat()) mo.disconnect();
      });
      mo.observe(document.body, { childList: true, subtree: false });
      /* Le widget se pose dans les premieres secondes ou pas du tout :
         inutile de laisser un observateur en vie pour la session. */
      setTimeout(function () { mo.disconnect(); }, 15000);
    }

    /* 2. Accuse de reception sur l'icone du compte. */
    document.querySelectorAll('header .header-account').forEach(function (a) {
      if (a.dataset.vjAttente) return;
      a.dataset.vjAttente = '1';
      a.addEventListener('click', function () {
        a.classList.add('is-loading');
        /* Si la personne revient en arriere, l'etat ne doit pas rester
           colle sur une page restauree depuis le cache. */
        setTimeout(function () { a.classList.remove('is-loading'); }, 12000);
      });
    });
    window.addEventListener('pageshow', function () {
      document.querySelectorAll('header .header-account.is-loading')
        .forEach(function (a) { a.classList.remove('is-loading'); });
    });
  }


  /* ---------------------------------------------------------------
     VJ-CORR-D519 — LA PISTE DU MENU D'ORDINATEUR ET SON CURSEUR
     ---------------------------------------------------------------
     Rachid (34e tour) : « un petit quelque chose qui entoure les
     sections quand on les survole, quelque chose de stylé ; j'aime
     pas le petit point gold, et là ça a l'air un peu vide ».
     Refusés avant : la bulle de verre qui se CRÉE sous chaque mot
     (D505), le losange d'or (D504/D511).
     Ici UN SEUL objet : une pastille crème pleine qui GLISSE d'une
     entrée à l'autre dans la piste en creux (vj-premium.css, D519),
     et se repose sur la page où l'on est. Le script ne fait que
     poser sa place et sa largeur ; le dessin et la course vivent
     dans le CSS (coupés sous prefers-reduced-motion).
     --------------------------------------------------------------- */
  function initCurseurMenu() {
    var ul = document.querySelector('header ul.dt-desktop-menu');
    if (!ul || ul.getAttribute('data-vj-curseur')) return;
    ul.setAttribute('data-vj-curseur', '1');
    var curseur = document.createElement('li');
    curseur.className = 'vj-nav-curseur';
    curseur.setAttribute('aria-hidden', 'true');
    curseur.setAttribute('role', 'presentation');
    ul.insertBefore(curseur, ul.firstChild);
    var vise = null;

    function entrees() {
      return Array.prototype.filter.call(ul.children, function (li) { return li !== curseur; });
    }
    function lienDe(li) { return li ? li.querySelector(':scope > a.nav-link') : null; }
    function actif() {
      var ouvert = ul.querySelector(':scope > li > a.nav-link[aria-expanded="true"]');
      return ouvert || ul.querySelector(':scope > li > a.nav-link.active');
    }
    function marquer(a) {
      Array.prototype.forEach.call(ul.querySelectorAll(':scope > li > a.vj-nav-sous'), function (x) {
        if (x !== a) x.classList.remove('vj-nav-sous');
      });
      if (a) a.classList.add('vj-nav-sous');
    }
    function viser(a, sec) {
      vise = a;
      if (!a || !a.offsetWidth) { curseur.classList.remove('est-pose'); marquer(null); return; }
      var ru = ul.getBoundingClientRect(), ra = a.getBoundingClientRect();
      var x = ra.left - ru.left - ul.clientLeft, y = ra.top - ru.top - ul.clientTop;
      var net = sec || !curseur.classList.contains('est-pose');
      if (net) curseur.classList.add('sans-glisser');
      curseur.style.width = ra.width + 'px';
      curseur.style.height = ra.height + 'px';
      curseur.style.transform = 'translate(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px)';
      if (net) { void curseur.offsetWidth; curseur.classList.remove('sans-glisser'); }
      curseur.classList.add('est-pose');
      marquer(a);
    }
    function entreeSous(cible) {
      var liste = entrees();
      for (var i = 0; i < liste.length; i++) if (liste[i].contains(cible)) return lienDe(liste[i]);
      return null;
    }
    ul.addEventListener('pointerover', function (e) {
      var a = entreeSous(e.target);
      if (a && a !== vise) viser(a);
    });
    ul.addEventListener('pointerleave', function () { viser(actif()); });
    ul.addEventListener('focusin', function (e) { var a = entreeSous(e.target); if (a) viser(a); });
    ul.addEventListener('focusout', function (e) { if (!ul.contains(e.relatedTarget)) viser(actif()); });
    function recaler() { viser(vise || actif(), true); }
    if ('ResizeObserver' in window) new ResizeObserver(recaler).observe(ul);
    window.addEventListener('resize', recaler);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(recaler);
    window.addEventListener('load', recaler);
    viser(actif(), true);
  }

  /* ---------------------------------------------------------------
     Amorçage
     --------------------------------------------------------------- */
  function init() {
    /* le header condensé n'est pas de la décoration : il reste actif
       même en mouvement réduit (il se contente alors de ne pas glisser) */
    initCondense();
    initCategories();
    initPhotosCategories();   /* D407 */
    initChatEtCompte();
    initRemonteePagination();
    initAffinerExclusif();
    initAffinerPanneau();
    initLibelleTri();
    initCurseurMenu();        /* D519 */

    if (reduce) return;

    initParallax();
    initReveal();
    initSheen();
    initTilt();
    initMagnetic();
    initMarquees();

    /* Les carrousels sont montes par theme.js apres le DOM : on laisse
       passer un tour de boucle avant de compter les vues par ecran. */
    window.setTimeout(function () {
      initSwipeHints();
      initScrollers();
      apaiserLesCarrousels();
    }, 600);
  }

  /* ---------------------------------------------------------------
     VJ-CORR-D164 — 5 septembre 2026 — ON NE REMONTE PAS EN HAUT
     ---------------------------------------------------------------
     Rachid : « lorsqu'on passe d'une page 1 a une page 2, on remonte
     pas automatiquement en haut, on reste en bas voire on descend
     encore plus bas. Le but c'est que lorsqu'on appuie sur la page 2,
     on remonte tout en haut, et comme ca le client il se scrolle vers
     le bas. Parce que si tu appuies sur deux et que tu descends la sur
     lien utile mention legale, on est pas sorti de l'auberge. »

     LA CAUSE, LUE DANS LE FICHIER. La pagination n'est PAS une
     navigation : `assets/wdt-filter.js` (classe `productPagination`)
     intercepte le clic avec `event.preventDefault()`, remplace la
     grille par un `fetchProducts()` en AJAX et reecrit l'URL avec
     `history.replaceState`. Aucun deplacement de la page n'est prevu
     nulle part. On reste donc exactement ou on etait — c'est-a-dire en
     bas, au niveau du pied de page, une fois la nouvelle grille en
     place et souvent plus courte.

     On n'edite pas `wdt-filter.js` : un ecouteur en phase de CAPTURE
     passe AVANT le sien, et la remontee part donc au moment du clic,
     pendant que la requete voyage. Rien a synchroniser.

     `scroll-behavior: smooth` est deja pose sur `html` par le projet :
     `scrollTo` sans option suffit, et respecte le mouvement reduit.
     --------------------------------------------------------------- */
  /* ---------------------------------------------------------------
     VJ-CORR-D165 — UN SEUL GROUPE D'AFFINER OUVERT A LA FOIS
     ---------------------------------------------------------------
     Rachid : « lorsqu'on deplie un et que l'autre est ouvert, il faut
     qu'il se ferme, on peut pas avoir les trois ouverts en meme temps. »

     Le travail est fait par l'attribut `name` pose sur les <details>
     dans vj-affiner.liquid : deux <details> de meme nom forment un
     accordeon exclusif, nativement. Ceci n'est qu'un repli pour les
     navigateurs qui ne connaissent pas encore cette regle — on le
     detecte plutot que de le supposer, et on ne branche rien s'il est
     inutile.
     --------------------------------------------------------------- */
  /* ---------------------------------------------------------------
     VJ-CORR-D165 — LA PASTILLE DE TRI GARDE SON LIBELLE
     ---------------------------------------------------------------
     `wdt-filter.js`, fonction `customSortSelect` (ligne 728), fait
     `button.textContent = selectedText` a chaque choix : la pastille
     reprendrait sa valeur (« Alphabetique, de A a Z ») des le premier
     tri, et « Trier » disparaitrait.

     On ne modifie pas leur fichier. Un ecouteur pose APRES le leur sur
     les memes <li> passe apres lui dans l'ordre de bulle, et repose le
     libellé. L'`aria-label` suit, pour que ce qui est annonce reste
     vrai. Le libellé vient de `data-vj-libelle`, ecrit dans le gabarit.
     --------------------------------------------------------------- */
  function initLibelleTri() {
    var bouton = document.querySelector('.custom-select-buttons[data-vj-libelle]');
    if (!bouton || bouton.dataset.vjLibelleInit) return;
    bouton.dataset.vjLibelleInit = '1';
    var libelle = bouton.dataset.vjLibelle;

    var liste = document.querySelector('.wdt-custom-sorting');
    if (!liste) return;

    liste.querySelectorAll('li').forEach(function (item) {
      item.addEventListener('click', function () {
        var valeur = item.textContent.trim();
        bouton.textContent = libelle;
        bouton.setAttribute('aria-label', libelle + ' — actuellement : ' + valeur);
      });
    });
  }

  /* ---------------------------------------------------------------
     VJ-CORR-D337 — 13 septembre 2026 — MARQUE ET CATEGORIE NE SAUTENT
     PLUS : LES BOUTONS RESTENT EN PLACE, LE PANNEAU S'OUVRE DESSOUS
     ---------------------------------------------------------------
     Rachid : « quand on clique sur MARQUE, il se place en dessous de
     CATEGORIE. Si je veux le refermer, j'ai le reflexe d'appuyer la ou
     j'ai appuye la premiere fois — sauf que la j'appuie sur CATEGORIE,
     et c'est CATEGORIE qui s'ouvre. »

     LA CAUSE : D165 envoyait le groupe OUVERT a la ligne (`order: 1`,
     `flex: 1 0 100%`) pour lui donner la largeur de ses pastilles. Le
     bouton sur lequel on venait d'appuyer quittait donc sa place, et
     l'autre prenait la sienne.

     LE REMEDE (assets/vj-premium.css, D337) : les deux boutons ne
     bougent plus jamais ; le panneau de valeurs est pose en absolu SOUS
     la rangee, sur toute sa largeur, avec une encoche sous le bouton
     ouvert. Ce script fait les deux choses que le CSS ne sait pas :
     1. reserver sous la rangee la HAUTEUR du panneau ouvert (sinon il
        recouvrirait les produits au lieu de les pousser) ;
     2. placer l'encoche au milieu du bouton ouvert.
     Plus Echap, qui referme et rend le focus au bouton.
     La grille est rechargee en AJAX par wdt-filter.js : on ecoute au
     niveau du document (`toggle` en capture) et on recale quand le DOM
     change, au lieu de brancher des ecouteurs sur des noeuds perissables.
     --------------------------------------------------------------- */
  function initAffinerPanneau() {
    if (window.vjAffinerPanneau) return;
    window.vjAffinerPanneau = true;
    var suivi = null;
    var ro = ('ResizeObserver' in window) ? new ResizeObserver(function () { caler(); }) : null;
    var prevu = false;

    function caler() {
      document.querySelectorAll('.vj-affiner__groupes').forEach(function (rangee) {
        var ouvert = rangee.querySelector('details.vj-affiner__groupe[open]');
        if (!ouvert) {
          rangee.classList.remove('a-un-ouvert');
          rangee.style.removeProperty('--vj-affiner-panneau');
          return;
        }
        var panneau = ouvert.querySelector('.vj-affiner__valeurs');
        var bouton = ouvert.querySelector('summary');
        if (!panneau || !bouton) return;
        rangee.classList.add('a-un-ouvert');
        rangee.style.setProperty('--vj-affiner-panneau', Math.ceil(panneau.offsetHeight) + 'px');
        var r = rangee.getBoundingClientRect();
        var b = bouton.getBoundingClientRect();
        rangee.style.setProperty('--vj-affiner-encoche', Math.round(b.left - r.left + b.width / 2) + 'px');
        if (ro && suivi !== panneau) {
          if (suivi) ro.unobserve(suivi);
          ro.observe(panneau);
          suivi = panneau;
        }
      });
    }
    function plusTard() {
      if (prevu) return;
      prevu = true;
      requestAnimationFrame(function () { prevu = false; caler(); });
    }

    document.addEventListener('toggle', function (e) {
      var d = e.target;
      if (d && d.matches && d.matches('details.vj-affiner__groupe')) caler();
    }, true);
    window.addEventListener('resize', plusTard, { passive: true });
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var ouvert = document.querySelector('details.vj-affiner__groupe[open]');
      if (!ouvert) return;
      ouvert.open = false;
      var s = ouvert.querySelector('summary');
      if (s) s.focus();
    });
    var zone = document.querySelector('.vj-affiner');
    zone = zone && zone.closest('.shopify-section') || document.body;
    if ('MutationObserver' in window) {
      new MutationObserver(plusTard).observe(zone, { childList: true, subtree: true });
    }
    caler();
  }

  function initAffinerExclusif() {
    var sonde = document.createElement('details');
    if ('name' in sonde) return;          /* le navigateur sait faire */

    var groupes = document.querySelectorAll('details[name="vj-affiner"]');
    if (groupes.length < 2) return;

    groupes.forEach(function (d) {
      d.addEventListener('toggle', function () {
        if (!d.open) return;
        groupes.forEach(function (autre) { if (autre !== d) autre.open = false; });
      });
    });
  }

  function initRemonteePagination() {
    var zone = document.querySelector('product-pagination');
    if (!zone || zone.dataset.vjRemontee) return;
    zone.dataset.vjRemontee = '1';

    zone.addEventListener('click', function (e) {
      var lien = e.target.closest && e.target.closest('.page-link');
      if (!lien) return;
      window.scrollTo({ top: 0, behavior: reduce ? 'auto' : 'smooth' });
    }, true);
  }

  /* ---------------------------------------------------------------
     VJ-CORR-D135 — 4 septembre 2026 — « CA DEFILE HYPER VITE »
     ---------------------------------------------------------------
     Rachid, pour la quatrieme fois : « lorsqu'on debouche sur cette
     page, le defilement des collections est trop rapide. D'un coup ca
     defile tout seul hyper vite, des qu'on actualise la page ca defile
     ca defile. Le but c'est qu'on voit qu'il y a les collections, que
     ca defile automatiquement, MAIS LENTEMENT. »

     CE QUE D34 (17 aout) AVAIT FAIT, ET POURQUOI CA NE MARCHAIT PAS.
     Il posait speed 900 / delay 8500 dans une fonction appelee 600 ms
     apres le chargement. Mesure du 4 septembre sur /collections/all,
     avances horodatees a l'evenement `slideChangeTransitionStart` :

         799 ms   -> vue 1
        1294 ms   -> vue 2
       10298 ms   -> vue 3
       19713 ms   -> vue 0
       params reels : speed 480, autoplay.delay = { delay: 5000 }

     DEUX pas en 1,3 seconde, puis un pas toutes les 9 s. Les 9 s, c'est
     D34 qui finit par prendre. Les 1,3 s, c'est ce que Rachid voit —
     et D34 arrivait 600 ms trop tard pour l'empecher.

     LA CAUSE DE LA RAFALE. `assets/theme.js` construit
     `autoplaySettings = { delay: 1000 * auto_play }` puis passe
     `autoplay: { delay: autoplaySettings }` : la valeur arrive donc a
     Swiper sous la forme **{ delay: { delay: 5000 } }**. Le delai n'est
     pas un nombre, Swiper ne peut pas l'attendre, et il enchaine.
     Releve tel quel ci-dessus, ce n'est pas une deduction. D34 corrigeait
     le symptome par-dessus, sans voir l'imbrication.

     CE QU'ON FAIT, ET DANS CET ORDRE.
     1. On prend la main a la PREMIERE IMAGE ou le carrousel existe, pas
        600 ms plus tard : boucle `requestAnimationFrame` lancee des
        l'execution du script, avant meme DOMContentLoaded.
     2. On ARRETE l'avance immediatement, avant qu'elle serve.
     3. On desimbrique le delai ({delay:{delay:N}} -> N) et on pose une
        cadence lente : glissade 1500 ms en `ease-in-out`, pause 7,5 s.
     4. Si la rafale a deja eu lieu (on est arrive apres), on RAMENE la
        vue a la premiere, sans animation : une page qui s'ouvre doit
        montrer le debut de ses collections, pas leur milieu.
     5. On ne redemarre l'avance QUE lorsque le carrousel est a l'ecran,
        et jamais avant 4 s de page. « Des qu'on actualise ca defile »
        cesse d'etre vrai : au chargement, rien ne bouge.
     6. Le premier geste de la personne rend la main : `stopOnLastSlide`
        n'a pas de sens ici, mais `disableOnInteraction` si — on ne
        reprend pas la main sur quelqu'un qui est en train de choisir.

     Les rails maison (`initScrollers`) gardent leurs 7,5 s : ils ne
     passent pas par Swiper et n'ont jamais eu la rafale.

     Rollback : supprimer cette fonction, son appel, et
     `window.requestAnimationFrame(guetterLesCarrousels)` plus bas.
     --------------------------------------------------------------- */
  var VJ_GLISSADE = 1500;   /* duree d'un pas AUTOMATIQUE, en ms */
  var VJ_POSE = 7500;       /* temps de lecture entre deux pas */
  var VJ_ATTENTE = 4000;    /* rien ne bouge pendant les N premieres ms */

  /* VJ-CORR-D406 — le heros de l'accueil, et lui seul, a sa propre cadence.
     Une vitrine qu'on vient d'ouvrir doit MONTRER qu'elle a plusieurs vues ;
     9 s de pose, personne ne les attend. 4,5 s de pose pour 1,5 s de glissade
     — la glissade ne bouge pas, c'est elle que « MAIS LENTEMENT » designe
     (D135). Et la main rendue au doigt revient au bout de douze secondes. */
  var VJ_POSE_HEROS = 4500;
  var VJ_ATTENTE_HEROS = 1700;
  var VJ_REPRISE = 12000;

  /* VJ-CORR-D153 — 4 septembre 2026 — LE DOIGT NE DOIT PAS ATTENDRE 1,5 s.
     ---------------------------------------------------------------
     Rachid : « sur la partie catalogue, lorsque je swipe par exemple
     les collections, la facon dont ca slide, c'est pas tres fluide. »

     CE QUE LA MESURE DIT, ET CE QU'ELLE NE DIT PAS. Ce n'est PAS un
     manque d'images par seconde. Releve sur /collections/all avec le
     processeur ralenti QUATRE FOIS, trames comptees pendant une
     transition : 82 trames, 16,7 ms de moyenne, soit **60 images par
     seconde**, pire trame 18,6 ms, **zero trame longue**. Le mouvement
     est parfaitement lisse.

     LA VRAIE CAUSE. `sw.params.speed` valait 1500. D135 l'avait pose la
     pour que le defilement AUTOMATIQUE soit lent — Rachid l'avait
     demande quatre fois : « ca defile automatiquement, MAIS LENTEMENT ».
     Sauf que cette meme valeur gouverne aussi la glissade declenchee par
     le DOIGT, par les fleches et par les puces. Un geste mettait donc une
     seconde et demie a se resoudre. Ce n'est pas une saccade, c'est une
     lenteur — et de l'interieur, ca se ressent exactement comme « pas
     fluide ».

     POURQUOI LES DEUX PEUVENT ETRE SEPARES SANS RIEN CASSER. L'avance
     automatique de D135 n'utilise deja PLUS `params.speed` : elle appelle
     `sw.slideNext(VJ_GLISSADE)` en passant sa duree en argument (voir
     `rendreLAvance` plus bas). `params.speed` ne sert donc plus qu'aux
     gestes. Le baisser laisse le defilement automatique exactement aussi
     lent qu'avant — c'est verifiable en une ligne, et c'est verifie.

     420 ms : au-dessus des 300 ms par defaut de Swiper, parce que la
     maison veut du mouvement tenu et pas un claquement ; largement sous
     le seuil ou un geste commence a se sentir en retard.

     Rollback : remettre `sw.params.speed = VJ_GLISSADE;` plus bas. */
  var VJ_GESTE = 420;       /* duree d'un pas demande PAR LA PERSONNE */

  var vjDepart = Date.now();
  var vjTenus = [];

  function calmerUnCarrousel(el) {
    var sw = el.swiper;
    if (!sw || !sw.params || el.dataset.vjCalme) return false;
    el.dataset.vjCalme = '1';

    /* On coupe toute avance native avant qu'elle serve. Les gabarits
       posent desormais auto_play: 0 ; ceci reste le filet si un reglage
       revient un jour par l'editeur. */
    if (sw.autoplay && sw.autoplay.stop) sw.autoplay.stop();
    if (sw.params.autoplay && typeof sw.params.autoplay === 'object') {
      sw.params.autoplay.disableOnInteraction = true;
    }
    sw.params.speed = VJ_GESTE;

    /* Si la rafale a eu lieu avant qu'on prenne la main, on revient a la
       premiere vue, sans animation : une page qui s'ouvre doit montrer le
       debut de ses collections, pas leur milieu. */
    if (Date.now() - vjDepart < 6000 && sw.realIndex > 0) {
      if (sw.params.loop && sw.slideToLoop) sw.slideToLoop(0, 0, false);
      else if (sw.slideTo) sw.slideTo(0, 0, false);
    }

    /* Un carrousel qui montre deja tout n'a rien a faire avancer. */
    var vues = el.querySelectorAll('.swiper-slide').length;
    var place = sw.params.slidesPerView;

    /* VJ-CORR-D175 — 6 septembre 2026 — LA GALERIE D'UNE FICHE PRODUIT
       N'AVANCE JAMAIS TOUTE SEULE.

       Elle a `slidesPerView: 1` et N photos : elle entrait donc dans la liste
       des carrousels qu'on fait avancer d'un pas toutes les neuf secondes.
       Consequence, les photos d'une piece changeaient pendant qu'on lisait sa
       description — et le defaut qu'on croit voir sur la photo 3 n'y est plus
       quand on veut y revenir. Sur du luxe d'occasion, ou l'etat EST le
       produit, c'est le contraire du service qu'on doit.

       La colonne de miniatures y entrait aussi des la cinquieme photo.

       Une vitrine qui defile est une invitation ; une piece unique qu'on
       examine, non. La regle ne change pas ailleurs. */
    var estFiche = el.classList.contains('product-media-gallery')
      || el.classList.contains('product-thumbnails');

    if (!estFiche && (typeof place !== 'number' || vues > place)) vjTenus.push(sw);

    rendreLAvance();   /* D406 : elle ne sert que ceux dont l'heure est venue */
    return true;
  }

  /* L'avance est la NOTRE : un pas toutes les VJ_POSE + VJ_GLISSADE, et
     seulement si le carrousel est a l'ecran et l'onglet au premier plan.
     Le premier geste de la personne rend la main pour de bon — on ne
     reprend pas la main sur quelqu'un qui est en train de choisir. */
  function rendreLAvance() {
    if (reduce) return;
    vjTenus.forEach(function (sw) {
      if (!sw.el || sw.el.dataset.vjAvance) return;
      /* D406 : chacun a son heure de depart. Le heros part a 1,7 s, les
         autres a 4 s — une seule fonction, deux seuils, et personne n'est
         servi avant le sien (sans quoi le reveil du heros demarrerait
         AUSSI les rails du catalogue, et la rafale de D135 reviendrait). */
      var sonAttente = sw.el.classList.contains('slideshow_swiper') ? VJ_ATTENTE_HEROS : VJ_ATTENTE;
      /* VJ-CORR-D465 — en mode LÉGER (l'onglet vient d'être tué, voir
         layout/theme.liquid), seul le héros garde son avance. */
      if (document.documentElement.classList.contains('vj-leger') && !sw.el.classList.contains('slideshow_swiper')) return;
      if (Date.now() - vjDepart < sonAttente) return;
      sw.el.dataset.vjAvance = '1';

      var aLEcran = true;
      var repris = false;

      if (window.IntersectionObserver) {
        aLEcran = false;
        new IntersectionObserver(function (entrees) {
          entrees.forEach(function (e) { aLEcran = e.isIntersecting; });
        }, { threshold: 0.35 }).observe(sw.el);
      }

      /* VJ-CORR-D406 — 19 septembre 2026 — LE HEROS DE L'ACCUEIL AVANCE DES
         L'ARRIVEE, ET UN DEFILEMENT DE PAGE NE L'ARRETE PAS.
         ---------------------------------------------------------------
         Rachid : « j'aimerais bien que ca defile automatiquement sur la
         premiere page, qu'on n'ait pas a faire glisser nous-memes — des
         que la personne atterrit sur la page. »

         CE QUE LA MESURE DISAIT (tmp-r23-avance.js, avant ce bloc) :
             params.autoplay.enabled = true   mais   autoplay.running = false
             indice   0s:0  4s:1  14s:2       soit un pas toutes les 9 s
         L'avance native est coupee par `calmerUnCarrousel` (c'est voulu,
         D135 : elle partait en RAFALE, deux pas en 1,3 s). Ce qui reste,
         c'est la notre : 4 s d'attente puis 9 s de pose. Sur le heros,
         personne ne reste neuf secondes sans toucher l'ecran — et la
         ligne d'en dessous rendait la main POUR DE BON au premier
         `touchstart` ou au premier `wheel`. Or sur un telephone, faire
         defiler la page COMMENCE par un `touchstart` sur le heros, et a
         la souris un `wheel` le traverse. L'avance mourait donc avant
         d'avoir servi : c'est exactement ce qu'il decrit.

         LA DISTINCTION QUI MANQUAIT : un geste qui traverse le carrousel
         n'est pas un geste QUI LE PILOTE. Swiper sait faire la
         difference — `sliderMove` ne se declenche que si le rail suit
         vraiment le doigt, et un clic de puce passe par `slideChange`
         avec `sw.animating`. On ecoute ces deux-la, pas le DOM brut.

         ET SUR LE HEROS SEUL, LA MAIN EST RENDUE POUR DOUZE SECONDES,
         PAS POUR TOUJOURS. « On ne reprend pas la main sur quelqu'un qui
         est en train de choisir » (D135 point 6) reste vrai le temps du
         choix ; passe ce delai, la vitrine redevient une vitrine. Les
         autres carrousels ne changent pas d'un iota : main rendue
         definitivement, 9 s de pose, 4 s d'attente.

         Rollback : remettre les deux lignes d'origine (ecouteurs DOM avec
         `once`, `repris` definitif) et retirer `estHeros`. */
      var estHeros = sw.el.classList.contains('slideshow_swiper');
      var reprisJusqua = 0;

      if (estHeros) {
        var rendreLaMain = function () {
          repris = true;
          reprisJusqua = Date.now() + VJ_REPRISE;
        };
        sw.on('sliderMove', rendreLaMain);
        sw.on('tap', rendreLaMain);
        sw.el.addEventListener('click', function (e) {
          if (e.target.closest('.swiper-pagination, .swiper-button-prev, .swiper-button-next')) rendreLaMain();
        }, { passive: true });
      } else {
        ['touchstart', 'mousedown', 'pointerdown', 'wheel'].forEach(function (evt) {
          sw.el.addEventListener(evt, function () { repris = true; }, { passive: true, once: true });
        });
      }

      var unPas = function () {
        if (estHeros && repris && Date.now() >= reprisJusqua) repris = false;
        if (repris || !aLEcran || document.hidden) return;
        if (sw.slideNext) sw.slideNext(VJ_GLISSADE);
      };

      if (estHeros) {
        /* D406 : le PREMIER pas du heros ne se fait pas attendre un cycle
           entier. Mesure d'avant ce bloc : depart a 1,7 s + 6 s de cycle =
           premier mouvement a 7,7 s, et « des que la personne atterrit »
           n'etait pas tenu. Un pas a 3 s, puis la cadence. Ce n'est PAS la
           rafale de D135 (deux pas en 1,3 s) : c'est un seul pas, apres
           que la page s'est posee. */
        window.setTimeout(function () {
          unPas();
          window.setInterval(unPas, VJ_POSE_HEROS + VJ_GLISSADE);
        }, 3000);
      } else {
        window.setInterval(unPas, VJ_POSE + VJ_GLISSADE);
      }
    });
  }

  function guetterLesCarrousels() {
    var reste = false;
    document.querySelectorAll('.swiper').forEach(function (el) {
      if (!el.dataset.vjCalme && !calmerUnCarrousel(el)) reste = true;
    });
    if (Date.now() - vjDepart < 6000) window.requestAnimationFrame(guetterLesCarrousels);
    else if (reste) window.setTimeout(guetterLesCarrousels, 400);
  }
  window.requestAnimationFrame(guetterLesCarrousels);
  window.setTimeout(rendreLAvance, VJ_ATTENTE_HEROS);   /* D406 */
  window.setTimeout(rendreLAvance, VJ_ATTENTE);

  function apaiserLesCarrousels() { guetterLesCarrousels(); }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);

  /* VJ-CORR-D374 — la pagination est remplacée avec la grille : l'écouteur
     de remontée (D164) posé sur l'ancienne `product-pagination` ne servait
     qu'au PREMIER changement de page. Le signal vient de vj-carte-carrousel.js. */
  window.addEventListener('vj:grille-remplacee', function () {
    initRemonteePagination();
    /* VJ-CORR-D460 — un onglet de « la sélection du moment » qui entre au
       clic porte un carrousel neuf : il reçoit ses puces et son indice comme
       celui qui était là au chargement (la pose est idempotente). */
    initSwipeHints();
  });

  /* Rebranchement après un rendu de section dans l'éditeur de thème */
  document.addEventListener('shopify:section:load', function () {
    initCategories();
    initPhotosCategories();   /* D407 */
    if (reduce) return;
    initReveal();
    initTilt();
    initMagnetic();
    initMarquees();
    initScrollers();
  });
})();
